import React from 'react';
import { ArrowRight, ArrowUpRight, Code, Cpu, Gamepad2, Palette, Sparkles, Zap, Play } from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import heroText from '../../assets/enigma-hero-text.png';
import schoolLogo from '../../assets/school-logo.png';

export const HomePage: React.FC = () => {
    const categories = [
        { icon: Code, title: 'Programming', desc: 'Competitive coding, hackathons, and algorithm challenges.', color: '#FF991C' },
        { icon: Cpu, title: 'Robotics', desc: 'Bot wars, line followers, and autonomous systems.', color: '#311b92' },
        { icon: Palette, title: 'Creative', desc: 'Digital art, video editing, and 3D modeling.', color: '#7C4DFF' },
        { icon: Gamepad2, title: 'Gaming', desc: 'E-sports tournaments and game development.', color: '#4527a0' },
    ];

    return (
        <PublicLayout>
            {/* ─── HERO SECTION ─── */}
            <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#0A0A0A]">
                {/* Ambient background */}
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full opacity-[0.12] blur-[150px]"
                        style={{ background: 'radial-gradient(ellipse, #FF991C 0%, #311b92 60%, transparent 100%)' }}
                    />
                </div>

                {/* Grid pattern */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:80px_80px]" />

                <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 flex flex-col items-center text-center">
                    {/* Presented by + School Logo */}
                    <div className="mb-12 animate-[fadeIn_0.8s_ease-out] flex flex-col items-center">
                        <p className="text-white/40 text-[11px] uppercase tracking-[0.35em] mb-5 font-medium">
                            Presented by
                        </p>
                        <div className="relative px-8 py-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] backdrop-blur-sm">
                            <img
                                src={schoolLogo}
                                alt="Seth M.R. Jaipuria School, Goel Campus, Lucknow"
                                className="w-[200px] sm:w-[240px] md:w-[280px] h-auto object-contain"
                            />
                        </div>
                    </div>

                    {/* Tagline */}
                    <div className="inline-flex items-center gap-3 mb-8 animate-[fadeInUp_0.8s_ease-out_0.15s_both]">
                        <span className="text-white/50 text-xs md:text-sm font-medium tracking-[0.3em] uppercase">Think</span>
                        <span className="w-1 h-1 rounded-full bg-[#FF991C]" />
                        <span className="text-white/50 text-xs md:text-sm font-medium tracking-[0.3em] uppercase">Solve</span>
                        <span className="w-1 h-1 rounded-full bg-[#311b92]" />
                        <span className="text-white/50 text-xs md:text-sm font-medium tracking-[0.3em] uppercase">Evolve</span>
                    </div>

                    {/* ENIGMA Logo */}
                    <div className="relative mb-6 animate-[fadeInUp_0.8s_ease-out_0.25s_both]">
                        <img
                            src={heroText}
                            alt="ENIGMA 2026"
                            className="w-[280px] sm:w-[380px] md:w-[500px] lg:w-[600px] h-auto object-contain"
                        />
                    </div>

                    {/* Editorial subtitle */}
                    <div className="mb-14 animate-[fadeInUp_0.8s_ease-out_0.35s_both]">
                        <p className="text-xl md:text-3xl leading-snug">
                            <span className="text-white/40 font-light">The Inter-School </span>
                            <span className="text-white font-semibold">Tech Symposium</span>
                        </p>
                    </div>

                    {/* CTAs */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-[fadeInUp_0.8s_ease-out_0.45s_both]">
                        <button
                            onClick={() => window.location.href = '/login'}
                            className="group relative px-8 py-4 bg-[#FF991C] text-white font-bold text-base rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(255,153,28,0.35)]"
                        >
                            <span className="relative z-10 flex items-center gap-2">
                                Register Now
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-[#FF991C] to-[#FFB84D] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                        <button
                            onClick={() => window.location.href = '/events'}
                            className="group px-8 py-4 bg-white/[0.04] text-white/80 font-semibold text-base rounded-2xl border border-white/10 hover:bg-white/[0.08] hover:border-white/20 hover:text-white transition-all duration-300"
                        >
                            <span className="flex items-center gap-2">
                                Explore Events
                                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </span>
                        </button>
                    </div>

                    {/* Registration badge */}
                    <div className="mt-10 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF991C]/[0.08] border border-[#FF991C]/15 animate-[fadeIn_0.8s_ease-out_0.6s_both]">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#FF991C] animate-pulse" />
                        <span className="text-[#FF991C]/80 text-xs font-semibold uppercase tracking-wider">Registration Open</span>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
                    <span className="text-white/20 text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                    <div className="w-px h-8 bg-gradient-to-b from-white/20 to-transparent" />
                </div>
            </section>

            {/* ─── ABOUT SECTION ─── */}
            <section className="relative py-28 md:py-36 bg-[#0A0A0A] overflow-hidden">
                {/* Subtle divider line */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] max-w-4xl h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

                <div className="max-w-[1200px] mx-auto px-6">
                    <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-16 lg:gap-20 items-center">
                        {/* Left — Text */}
                        <div>
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/[0.06] mb-10">
                                <Sparkles className="w-3.5 h-3.5 text-[#7C4DFF]" />
                                <span className="text-[11px] text-white/50 uppercase tracking-[0.2em] font-medium">About Enigma</span>
                            </div>

                            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-black text-white leading-[0.95] mb-4">
                                Beyond the
                            </h2>
                            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-black leading-[0.95] mb-10">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF991C] via-[#FFB84D] to-[#7C4DFF]">
                                    Ordinary
                                </span>
                            </h2>

                            <p className="text-base md:text-lg text-white/45 leading-relaxed mb-12 max-w-lg">
                                Enigma isn't just a competition — it's a crucible for the next generation of innovators.
                                We challenge you to push boundaries, think critically, and build solutions that matter.
                            </p>

                            {/* Feature list */}
                            <div className="space-y-1">
                                {[
                                    { title: 'Innovation First', desc: 'Challenges designed to test creativity and problem-solving.', color: '#FF991C' },
                                    { title: 'Technical Excellence', desc: 'From coding to robotics, showcase your prowess.', color: '#311b92' },
                                    { title: 'Future Ready', desc: 'Gain skills that prepare you for the tech world.', color: '#7C4DFF' },
                                ].map((item, i) => (
                                    <div
                                        key={i}
                                        className="group flex items-start gap-4 p-4 -mx-4 rounded-xl hover:bg-white/[0.02] transition-colors"
                                    >
                                        <div
                                            className="w-2.5 h-2.5 rounded-full mt-[7px] shrink-0 group-hover:scale-125 transition-transform"
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <div>
                                            <h3 className="text-white font-semibold text-[15px] mb-0.5">{item.title}</h3>
                                            <p className="text-white/35 text-sm leading-relaxed">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right — Decorative panel */}
                        <div className="relative">
                            <div
                                className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/[0.06]"
                                style={{
                                    background: 'linear-gradient(160deg, rgba(255,153,28,0.05) 0%, rgba(49,27,146,0.08) 50%, rgba(124,77,255,0.04) 100%)'
                                }}
                            >
                                {/* Large decorative letter */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-[18rem] md:text-[22rem] font-display font-black text-white/[0.025] leading-none select-none">
                                        E
                                    </span>
                                </div>

                                {/* Info cards stack */}
                                <div className="relative z-10 h-full p-8 flex flex-col justify-between">
                                    <div className="space-y-3">
                                        {[
                                            { label: 'Categories', value: '4 Tracks', color: '#FF991C' },
                                            { label: 'Format', value: '2-Day Event', color: '#311b92' },
                                            { label: 'Open To', value: 'All Schools', color: '#7C4DFF' },
                                        ].map((item, i) => (
                                            <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.04]">
                                                <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-white/30 text-[11px] uppercase tracking-wider">{item.label}</span>
                                                    <span className="text-white text-sm font-semibold">{item.value}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex items-center gap-3 px-4 py-4 rounded-xl bg-[#FF991C]/[0.06] border border-[#FF991C]/10">
                                        <Zap className="w-5 h-5 text-[#FF991C]" />
                                        <div>
                                            <p className="text-white text-sm font-bold">Multiple Categories</p>
                                            <p className="text-white/35 text-[11px]">Tech &middot; Creative &middot; Sports</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Decorative circles */}
                                <div className="absolute top-6 right-6 w-20 h-20 border border-[#FF991C]/8 rounded-full" />
                                <div className="absolute bottom-16 left-6 w-14 h-14 border border-[#311b92]/10 rounded-full" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── CATEGORIES SECTION ─── */}
            <section className="relative py-28 md:py-36 bg-[#0A0A0A]">
                {/* Subtle divider */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] max-w-4xl h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

                <div className="max-w-[1200px] mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-black text-white mb-4">
                            Event Categories
                        </h2>
                        <p className="text-base md:text-lg text-white/35 max-w-md mx-auto">
                            Four tracks, endless possibilities. Choose your arena.
                        </p>
                    </div>

                    {/* Uniform editorial grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {categories.map((cat, i) => {
                            const Icon = cat.icon;
                            return (
                                <div
                                    key={i}
                                    className="group relative rounded-2xl overflow-hidden cursor-default transition-all duration-500 hover:scale-[1.02]"
                                    style={{
                                        background: `linear-gradient(160deg, ${cat.color}08 0%, transparent 60%)`,
                                        border: `1px solid ${cat.color}15`
                                    }}
                                >
                                    {/* Hover glow */}
                                    <div
                                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                        style={{
                                            background: `radial-gradient(circle at 50% 80%, ${cat.color}12 0%, transparent 70%)`
                                        }}
                                    />

                                    <div className="relative z-10 p-7 flex flex-col min-h-[220px] justify-between">
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center mb-8 transition-transform duration-300 group-hover:scale-110"
                                            style={{ backgroundColor: `${cat.color}15` }}
                                        >
                                            <Icon className="w-6 h-6" style={{ color: cat.color }} />
                                        </div>

                                        <div>
                                            <h3 className="text-xl font-display font-bold text-white mb-2">{cat.title}</h3>
                                            <p className="text-white/35 text-sm leading-relaxed">{cat.desc}</p>
                                        </div>
                                    </div>

                                    {/* Corner accent */}
                                    <div
                                        className="absolute -bottom-8 -right-8 w-28 h-28 rounded-full blur-3xl opacity-15 group-hover:opacity-30 transition-opacity"
                                        style={{ backgroundColor: cat.color }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ─── CTA SECTION ─── */}
            <section className="relative py-28 md:py-36 bg-[#0A0A0A] overflow-hidden">
                {/* Background glow */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-r from-[#FF991C]/[0.08] via-[#311b92]/[0.06] to-[#7C4DFF]/[0.08] blur-[150px]" />
                </div>

                {/* Subtle divider */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] max-w-4xl h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

                <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#311b92]/[0.08] border border-[#311b92]/15 mb-10">
                        <Play className="w-3.5 h-3.5 text-[#9575CD]" />
                        <span className="text-[11px] text-[#9575CD] font-semibold uppercase tracking-[0.2em]">Limited Spots</span>
                    </div>

                    <h2 className="text-4xl md:text-6xl lg:text-7xl font-display font-black text-white leading-[0.95] mb-4">
                        Ready to
                    </h2>
                    <h2 className="text-4xl md:text-6xl lg:text-7xl font-display font-black leading-[0.95] mb-10">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF991C] to-[#311b92]">Compete?</span>
                    </h2>

                    <p className="text-base md:text-lg text-white/40 max-w-xl mx-auto mb-12 leading-relaxed">
                        Registration is now open for all schools. Get your access key from the invitation letter and register your team today.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button
                            onClick={() => window.location.href = '/login'}
                            className="group relative px-10 py-5 bg-gradient-to-r from-[#FF991C] to-[#E68A00] text-white font-bold text-base rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_50px_rgba(255,153,28,0.3)]"
                        >
                            <span className="relative z-10 flex items-center gap-3">
                                School Login
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </button>
                        <button
                            onClick={() => window.location.href = '/instructions'}
                            className="px-10 py-5 text-white/50 font-semibold text-base hover:text-white transition-colors"
                        >
                            View Instructions →
                        </button>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
};
